import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  Eye, 
  FileText, 
  Music, 
  Video, 
  Image, 
  Link as LinkIcon,
  Filter,
  Search,
  Upload,
  X,
  Book,
  Heart,
  MessageCircle,
  Church,
  Star,
  AlertCircle,
  Home,
  ChevronRight,
  LogOut,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Loader
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminResources = () => {
  const { user, token, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'document',
    category: 'formation',
    author: '',
    published_date: '',
    is_public: true,
    is_member: false,
    file_url: '',
    file: null,
    tags: '',
    notes: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    status: 'all',
    access_level: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Réduire à 5 pour voir la pagination plus facilement
  const [totalPages, setTotalPages] = useState(0);
  const [paginatedResources, setPaginatedResources] = useState([]);

  // Options de configuration
  const typeOptions = {
    document: { label: 'Document', icon: FileText, color: 'text-blue-600' },
    audio: { label: 'Audio', icon: Music, color: 'text-green-600' },
    video: { label: 'Vidéo', icon: Video, color: 'text-red-600' },
    image: { label: 'Image', icon: Image, color: 'text-purple-600' },
    link: { label: 'Lien', icon: LinkIcon, color: 'text-orange-600' }
  };

  const categoryOptions = {
    formation: { label: 'Formation', icon: Book, color: 'bg-blue-100 text-blue-800' },
    priere: { label: 'Prière', icon: Heart, color: 'bg-pink-100 text-pink-800' },
    temoignage: { label: 'Témoignage', icon: MessageCircle, color: 'bg-green-100 text-green-800' },
    liturgie: { label: 'Liturgie', icon: Church, color: 'bg-purple-100 text-purple-800' },
    catechese: { label: 'Catéchèse', icon: Star, color: 'bg-yellow-100 text-yellow-800' },
    spiritualite: { label: 'Spiritualité', icon: Heart, color: 'bg-indigo-100 text-indigo-800' },
    autre: { label: 'Autre', icon: FileText, color: 'bg-gray-100 text-gray-800' }
  };

  const accessLevelOptions = {
    public: { label: 'Public', color: 'bg-green-100 text-green-800' },
    member: { label: 'Membres', color: 'bg-blue-100 text-blue-800' },
    admin: { label: 'Administrateurs', color: 'bg-red-100 text-red-800' }
  };

  const VisibilityBadges = ({ resource }) => {
    const levels = [];
    if (resource.is_public) levels.push('public');
    if (resource.is_member) levels.push('member');
    if (levels.length === 0) levels.push('admin');
    return levels.map(level => (
      <span key={level} className={`inline-flex items-center mr-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${accessLevelOptions[level].color}`}>
        {accessLevelOptions[level].label}
      </span>
    ));
  };

  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:8000/api/resources', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setResources(data.data.data || []);
      } else {
        console.error('Erreur lors du chargement des ressources');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const applyFilters = useCallback(() => {
    let filtered = resources;

    // Filtre par type
    if (filters.type !== 'all') {
      filtered = filtered.filter(resource => resource.type === filters.type);
    }

    // Filtre par catégorie
    if (filters.category !== 'all') {
      filtered = filtered.filter(resource => resource.category === filters.category);
    }

    // Filtre par statut
    if (filters.status !== 'all') {
      filtered = filtered.filter(resource => resource.status === filters.status);
    }

    // Filtre par niveau d'accès
    if (filters.access_level !== 'all') {
      filtered = filtered.filter(resource => {
        if (filters.access_level === 'public') return !!resource.is_public;
        if (filters.access_level === 'member') return !!resource.is_member;
        return !resource.is_public && !resource.is_member;
      });
    }

    // Recherche textuelle
    if (searchTerm) {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(resource => 
        resource.title?.toLowerCase().includes(lowercaseSearchTerm) ||
        resource.description?.toLowerCase().includes(lowercaseSearchTerm) ||
        resource.author?.toLowerCase().includes(lowercaseSearchTerm) ||
        (Array.isArray(resource.tags) && resource.tags.some(tag => 
          tag.toLowerCase().includes(lowercaseSearchTerm)
        ))
      );
    }

    setFilteredResources(filtered);
    setCurrentPage(1); // Reset à la première page après filtrage
  }, [resources, filters, searchTerm]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    applyFilters();
  }, [resources, filters, searchTerm]);

  // Effet pour la pagination
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filteredResources.slice(startIndex, endIndex);
    setPaginatedResources(paginated);
    setTotalPages(Math.ceil(filteredResources.length / itemsPerPage));
  }, [filteredResources, currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true); // Démarrer l'état de chargement

    if (!formData.title.trim()) {
      setFormError('Le titre est requis');
      setIsSubmitting(false);
      return;
    }

    // Validation de la taille du fichier côté client
    if (formData.file && formData.file.size > 50 * 1024 * 1024) { // 50MB
      const fileSizeMB = (formData.file.size / 1024 / 1024).toFixed(2);
      setFormError(`Le fichier est trop volumineux (${fileSizeMB} MB). Taille maximum autorisée : 50MB`);
      setIsSubmitting(false);
      return;
    }

    try {
      // Simulation de progression pour les uploads de fichiers
      if (formData.file) {
        setUploadProgress(10);
        await new Promise(resolve => setTimeout(resolve, 200)); // Simulation
        setUploadProgress(30);
      }

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'is_public' || key === 'is_member') {
          submitData.append(key, formData[key] ? '1' : '0');
        } else if (key === 'file' && formData[key]) {
          submitData.append(key, formData[key]);
        } else if (key !== 'file' && formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      const url = selectedResource 
        ? `http://localhost:8000/api/resources/${selectedResource.id}`
        : 'http://localhost:8000/api/resources';

      const method = selectedResource ? 'PUT' : 'POST';

      // Pour PUT, on ne peut pas utiliser FormData avec _method
      let requestOptions;
      if (selectedResource) {
        requestOptions = {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: formData.title,
            description: formData.description,
            type: formData.type,
            category: formData.category,
            author: formData.author,
            published_date: formData.published_date,
            is_public: formData.is_public,
            is_member: formData.is_member,
            file_url: formData.file_url,
            tags: formData.tags,
            notes: formData.notes
          })
        };
      } else {
        requestOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: submitData
        };
      }

      // Progression avant l'envoi
      if (formData.file) {
        setUploadProgress(60);
      }

      const response = await fetch(url, requestOptions);
      
      // Progression après l'envoi
      if (formData.file) {
        setUploadProgress(90);
      }
      
      const data = await response.json();
      
      // Progression finale
      if (formData.file) {
        setUploadProgress(100);
      }
      
      if (data.success) {
        await fetchResources();
        setShowModal(false);
        resetForm();
      } else {
        // Gestion spécifique des erreurs de taille
        if (data.message && (
          data.message.includes('too large') || 
          data.message.includes('POST data is too large') ||
          data.message.includes('413')
        )) {
          setFormError('Le fichier est trop volumineux. Taille maximum : 50MB');
        } else {
          setFormError(data.message || 'Une erreur est survenue');
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      // Gestion spécifique des erreurs réseau liées à la taille
      if (error.toString().includes('413') || error.toString().includes('too large')) {
        setFormError('Le fichier est trop volumineux. Vérifiez la taille (max 50MB) et réessayez.');
      } else {
        setFormError('Erreur de connexion');
      }
    } finally {
      setIsSubmitting(false); // Arrêter l'état de chargement dans tous les cas
    }
  };

  const handleDelete = async () => {
    if (!selectedResource) return;

    try {
      const response = await fetch(`http://localhost:8000/api/resources/${selectedResource.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchResources();
        setShowDeleteModal(false);
        setSelectedResource(null);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'document',
      category: 'formation',
      author: '',
      published_date: '',
      is_public: true,
      is_member: false,
      file_url: '',
      file: null,
      tags: '',
      notes: ''
    });
    setSelectedResource(null);
    setFormError('');
    setIsSubmitting(false); // Réinitialiser l'état de chargement
    setUploadProgress(0); // Réinitialiser la progression
  };

  const openEditModal = (resource) => {
    setSelectedResource(resource);
    setFormData({
      title: resource.title || '',
      description: resource.description || '',
      type: resource.type || 'document',
      category: resource.category || 'formation',
      author: resource.author || '',
      published_date: resource.published_date || '',
      is_public: resource.is_public ?? resource.access_level === 'public',
      is_member: resource.is_member ?? resource.access_level === 'member',
      file_url: resource.file_url || '',
      file: null,
      tags: Array.isArray(resource.tags) ? resource.tags.join(', ') : (resource.tags || ''),
      notes: resource.notes || ''
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleLogout = () => {
    // Fonction de déconnexion (à adapter selon votre contexte d'auth)
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  const getTypeIcon = (type) => {
    const TypeIcon = typeOptions[type]?.icon || FileText;
    return <TypeIcon className={`w-5 h-5 ${typeOptions[type]?.color || 'text-gray-600'}`} />;
  };

  if (!user || !['admin', 'apd', 'apz', 'tgd', 'tgz', 'mcd', 'mcz'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Accès non autorisé</h3>
          <p className="mt-1 text-sm text-gray-500">
            Vous n'avez pas les permissions pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2 text-gray-600 text-sm">
                <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium text-gray-900 hidden sm:inline">Dashboard</span>
                <span className="font-medium text-gray-900 sm:hidden">Admin</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-purple-600 font-semibold text-sm sm:text-base">Ressources</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 w-full sm:w-auto">
              <div className="text-left sm:text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 flex items-center">
                  <FileText className="w-3 h-3 mr-1 text-blue-500" />
                  <span className="hidden sm:inline">Gestion Ressources FSF</span>
                  <span className="sm:hidden">Ressources FSF</span>
                </p>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Retour au Dashboard</span>
                  <span className="sm:hidden">Retour</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                  <span className="sm:hidden">Quitter</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>


      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* En-tête */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6 text-justify">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                <span className="hidden sm:inline">Gestion des Ressources</span>
                <span className="sm:hidden">Ressources</span>
              </h1>
              <p className="text-gray-600 text-justify text-sm sm:text-base">
                <span className="hidden sm:inline">Gérez la bibliothèque numérique de la communauté FSF</span>
                <span className="sm:hidden">Bibliothèque numérique FSF</span>
              </p>
            </div>
            {!isReadOnly && (
            <button
              onClick={openAddModal}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Ajouter une ressource</span>
              <span className="sm:hidden">Nouvelle ressource</span>
            </button>
            )}
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <div className="ml-4 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500">Total Ressources</dt>
                  <dd className="text-lg sm:text-2xl font-semibold text-gray-900">{resources.length}</dd>
                </dl>
              </div>
            </div>
          </div>

          {filteredResources.length !== resources.length && (
            <div className="bg-amber-50 p-4 sm:p-6 rounded-lg shadow border border-amber-200 ">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Filter className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-amber-700">Ressources Filtrées</dt>
                    <dd className="text-lg sm:text-2xl font-semibold text-amber-900">{filteredResources.length}</dd>
                    <dd className="text-xs text-amber-600 mt-1">
                      Page {currentPage} sur {totalPages} ({itemsPerPage} par page)
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          )}
          
          {Object.entries(typeOptions).map(([type, config]) => {
            const count = resources.filter(r => r.type === type).length;
            if (count === 0) return null;
            return (
              <div key={type} className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <config.icon className={`h-6 w-6 sm:h-8 sm:w-8 ${config.color}`} />
                  </div>
                  <div className="ml-4 sm:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">{config.label}</dt>
                      <dd className="text-lg sm:text-2xl font-semibold text-gray-900">{count}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Recherche
              </label>
              <input
                type="text"
                placeholder="Titre, description, auteur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Tous les types</option>
                {Object.entries(typeOptions).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Toutes les catégories</option>
                {Object.entries(categoryOptions).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="archived">Archivé</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accès</label>
              <select
                value={filters.access_level}
                onChange={(e) => setFilters({...filters, access_level: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Tous les niveaux</option>
                {Object.entries(accessLevelOptions).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Liste des ressources */}
        <div className="bg-white shadow rounded-lg text-justify">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 mt-2">Chargement des ressources...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune ressource trouvée</h3>
              {!isReadOnly && (
              <p className="mt-1 text-sm text-gray-500">
                Commencez par ajouter votre première ressource.
              </p>
              )}
            </div>
          ) : (
            <div>
              {/* Vue Desktop (Tableau) */}
              <div className="hidden lg:block overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ressource
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type / Catégorie
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Auteur / Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Accès / Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vues / Téléchargements
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedResources.map((resource) => (
                        <tr key={resource.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {getTypeIcon(resource.type)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                  {resource.title}
                                </p>
                                {resource.description && (
                                  <p className="text-sm text-gray-500 line-clamp-2 text-justify">
                                    {resource.description}
                                  </p>
                                )}
                                {resource.tags && Array.isArray(resource.tags) && resource.tags.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {resource.tags.map((tag, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeOptions[resource.type]?.color || 'text-gray-600'} bg-gray-100`}>
                                {typeOptions[resource.type]?.label || resource.type}
                              </span>
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryOptions[resource.category]?.color || 'bg-gray-100 text-gray-800'}`}>
                                  {categoryOptions[resource.category]?.label || resource.category}
                                </span>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {resource.author && (
                              <div>{resource.author}</div>
                            )}
                            {resource.published_date && (
                              <div className="text-xs">
                                {new Date(resource.published_date).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              Créé le {new Date(resource.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          </td>
                          
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <VisibilityBadges resource={resource} />
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  resource.status === 'active' ? 'bg-green-100 text-green-800' :
                                  resource.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {resource.status === 'active' ? 'Actif' : 
                                   resource.status === 'inactive' ? 'Inactif' : 'Archivé'}
                                </span>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center bg-blue-50 px-2 py-1 rounded-full">
                                  <Eye className="w-3 h-3 text-blue-600 mr-1" />
                                  <span className="text-blue-800 font-medium">{resource.view_count || 0}</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center bg-green-50 px-2 py-1 rounded-full">
                                  <Download className="w-3 h-3 text-green-600 mr-1" />
                                  <span className="text-green-800 font-medium">{resource.download_count || 0}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 text-right text-sm font-medium">
                            {!isReadOnly && (
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => openEditModal(resource)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedResource(resource);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Vue Mobile (Cards) */}
              <div className="lg:hidden">
                <div className="p-4 space-y-4">
                  {paginatedResources.map((resource) => (
                    <div key={resource.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="flex-shrink-0">
                            {getTypeIcon(resource.type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-medium text-gray-900 mb-1">
                              {resource.title}
                            </h3>
                            {resource.description && (
                              <p className="text-sm text-gray-500 mb-2 text-justify line-clamp-2">
                                {resource.description}
                              </p>
                            )}
                          </div>
                        </div>
                        {!isReadOnly && (
                        <div className="flex space-x-2 ml-2">
                          <button
                            onClick={() => openEditModal(resource)}
                            className="text-indigo-600 hover:text-indigo-900 p-1"
                            title="Modifier"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedResource(resource);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        )}
                      </div>

                      {/* Type et Catégorie */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeOptions[resource.type]?.color || 'text-gray-600'} bg-gray-100`}>
                          {typeOptions[resource.type]?.label || resource.type}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryOptions[resource.category]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {categoryOptions[resource.category]?.label || resource.category}
                        </span>
                      </div>

                      {/* Statut et Accès */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        <VisibilityBadges resource={resource} />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          resource.status === 'active' ? 'bg-green-100 text-green-800' :
                          resource.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {resource.status === 'active' ? 'Actif' : 
                           resource.status === 'inactive' ? 'Inactif' : 'Archivé'}
                        </span>
                      </div>

                      {/* Statistiques */}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex space-x-4">
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 text-blue-600 mr-1" />
                            <span>{resource.view_count || 0} vues</span>
                          </div>
                          <div className="flex items-center">
                            <Download className="w-4 h-4 text-green-600 mr-1" />
                            <span>{resource.download_count || 0} dl</span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {resource.author && <span>{resource.author}</span>}
                          {resource.published_date && (
                            <span className="block">
                              {new Date(resource.published_date).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Tags */}
                      {resource.tags && Array.isArray(resource.tags) && resource.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {resource.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pagination */}
              {filteredResources.length > itemsPerPage && (
                <div className="bg-white px-4 py-3 border-t border-gray-200">
                  {/* Mobile Pagination */}
                  <div className="flex justify-between items-center sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                  
                  {/* Desktop Pagination */}
                  <div className="hidden sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Affichage de{' '}
                        <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                        {' '}à{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * itemsPerPage, filteredResources.length)}
                        </span>
                        {' '}sur{' '}
                        <span className="font-medium">{filteredResources.length}</span>
                        {' '}résultats
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        {/* Première page */}
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronsLeft className="h-5 w-5" />
                        </button>
                        
                        {/* Page précédente */}
                        <button
                          onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>

                        {/* Numéros de page */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Afficher les pages proches de la page actuelle
                            return Math.abs(page - currentPage) <= 2 || page === 1 || page === totalPages;
                          })
                          .map((page, index, array) => {
                            // Ajouter "..." si nécessaire
                            const showEllipsis = index > 0 && page - array[index - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && (
                                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    currentPage === page
                                      ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}

                        {/* Page suivante */}
                        <button
                          onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                        
                        {/* Dernière page */}
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronsRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'ajout/modification */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center pb-3">
              <h3 className="text-lg font-medium">
                {selectedResource ? 'Modifier la ressource' : 'Ajouter une ressource'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{formError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 relative">
              {/* Overlay de chargement */}
              {isSubmitting && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="flex flex-col items-center space-y-4 w-64">
                    <Loader className="w-8 h-8 animate-spin text-blue-600" />
                    <p className="text-sm text-gray-600 font-medium text-center">
                      {formData.file ? 'Upload en cours...' : 'Enregistrement en cours...'}
                    </p>
                    {formData.file && (
                      <div className="w-full">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progression</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Titre de la ressource"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Description de la ressource"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(typeOptions).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie *
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(categoryOptions).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Auteur */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auteur
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nom de l'auteur"
                  />
                </div>

                {/* Date de publication */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de publication
                  </label>
                  <input
                    type="date"
                    value={formData.published_date}
                    onChange={(e) => setFormData({...formData, published_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Visibilité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibilité
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Public</span>
                      <span className="block text-xs text-gray-500">Affichée sur la page publique des ressources</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.is_member}
                      onChange={(e) => setFormData({...formData, is_member: e.target.checked})}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">Membres</span>
                      <span className="block text-xs text-gray-500">Accessible aux membres connectés depuis leur espace personnel</span>
                    </span>
                  </label>
                </div>
                {!formData.is_public && !formData.is_member && (
                  <p className="mt-2 text-xs text-orange-600">Sans aucune option cochée, la ressource n'est visible que par l'administration.</p>
                )}
              </div>

              {/* Fichier ou URL */}
              {!selectedResource && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fichier
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                          <span>Télécharger un fichier</span>
                          <input
                            type="file"
                            className="sr-only"
                            onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                            accept="*/*"
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        Maximum 50MB
                      </p>
                    </div>
                  </div>
                  {formData.file && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-600">
                        Fichier sélectionné: {formData.file.name}
                      </p>
                      <p className={`text-xs ${
                        formData.file.size > 50 * 1024 * 1024 
                          ? 'text-red-600' 
                          : 'text-green-600'
                      }`}>
                        Taille: {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        {formData.file.size > 50 * 1024 * 1024 && ' (Trop volumineux!)'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* URL externe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL externe (optionnel)
                </label>
                <input
                  type="url"
                  value={formData.file_url}
                  onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://exemple.com/document.pdf"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (séparés par des virgules)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="formation, prière, couple"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes internes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Notes pour l'administration"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    isSubmitting 
                      ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                      : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center justify-center space-x-2 ${
                    isSubmitting 
                      ? 'bg-blue-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting && (
                    <Loader className="w-4 h-4 animate-spin" />
                  )}
                  <span>
                    {isSubmitting 
                      ? 'Traitement en cours...' 
                      : selectedResource ? 'Mettre à jour' : 'Ajouter'
                    }
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <AlertCircle className="mx-auto h-16 w-16 text-red-400" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Supprimer la ressource
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 text-justify">
                  Êtes-vous sûr de vouloir supprimer "{selectedResource?.title}" ? 
                  Cette action est irréversible.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResources;